import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, KeyRound } from "lucide-react";
import { Link } from "react-router-dom";
import { AIRBNB_URL, PROPERTY, HERO_SLIDES } from "@/data/property";
import { MaskLine } from "./Reveal";

export default function Hero() {
  const [slide, setSlide] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 180]);
  const scale = useTransform(scrollY, [0, 800], [1, 1.15]);
  const overlayOpacity = useTransform(scrollY, [0, 500], [1, 0.6]);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
      {/* Parallax slideshow */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        {HERO_SLIDES.map((src, i) => (
          <motion.img
            key={src}
            src={src}
            alt="Waterfront luxury retreat with pool at golden hour"
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: slide === i ? 1 : 0 }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
          />
        ))}
      </motion.div>

      {/* Overlay */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/50 via-[#0A192F]/25 to-[#0A192F]/60"
      />

      {/* Content */}
      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
        <h1 className="font-display font-semibold text-white tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-8xl max-w-5xl [text-shadow:_0_2px_30px_rgba(10,25,47,0.4)]">
          <MaskLine delay={0.35}>{PROPERTY.headline[0]}</MaskLine>
          <MaskLine delay={0.5}>
            <span className="font-serif-accent italic font-normal text-[#1FB6C9]">
              {PROPERTY.headline[1]}
            </span>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 max-w-xl text-lg md:text-xl text-white/90 font-body [text-shadow:_0_1px_12px_rgba(10,25,47,0.5)]"
        >
          {PROPERTY.subheadline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href={AIRBNB_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-[#1FB6C9] px-8 py-4 font-display font-medium text-white shadow-xl shadow-[#1FB6C9]/30 transition-transform duration-300 hover:-translate-y-1"
          >
            Book Now
            <ArrowRight size={18} className="cta-arrow" />
          </a>
          <a
            href="#availability"
            data-testid="hero-availability-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/60 px-8 py-4 font-display font-medium text-white transition-colors duration-300 hover:bg-white hover:text-[#0E2A47]"
          >
            Check Availability
          </a>
          <Link
            to="/login"
            data-testid="hero-guest-checkin-btn"
            className="sm:ml-auto inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/40 px-8 py-4 font-display font-medium text-white transition-colors duration-300 hover:bg-white hover:text-[#0E2A47]"
          >
            <KeyRound size={16} /> Guest guide
          </Link>
        </motion.div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 right-5 md:right-10 z-10 flex gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setSlide(i)}
            data-testid={`hero-slide-${i}`}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              slide === i ? "w-8 bg-[#1FB6C9]" : "w-3 bg-white/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
